// Belt Configurator — Alpine.js state machine.
// Wizard reads belt-catalog.json (built by Shopify/build_belt_catalog.js).
// State: progressive filtering through 7 steps → resolves to one product+variant.

window.beltConfigurator = function ({ catalogUrl }) {
  return {
    // -------- State --------
    catalog: null,
    loading: true,
    currentStep: 1,
    selections: {
      width: null,
      color: null,
      withBuckle: null,
      buckleStyle: null,
      hardwareType: null,
      hardwareColor: null,
      size: null,
    },

    // -------- Step definitions --------
    steps: [
      { key: 'width', label: 'Width', prompt: 'Choose belt width', formatter: (v) => v + '”' },
      { key: 'color', label: 'Leather color', prompt: 'Choose leather color' },
      { key: 'withBuckle', label: 'Buckle', prompt: 'With or without buckle?', formatter: (v) => v ? 'With buckle' : 'No buckle' },
      { key: 'buckleStyle', label: 'Buckle style', prompt: 'Choose buckle style', skipIf: (s) => s.withBuckle === false },
      { key: 'hardwareType', label: 'Hardware', prompt: 'Choose hardware type' },
      { key: 'hardwareColor', label: 'Hardware finish', prompt: 'Choose hardware color' },
      { key: 'size', label: 'Size', prompt: 'Choose your size' },
    ],

    // -------- Lifecycle --------
    async init() {
      try {
        const r = await fetch(catalogUrl, { cache: 'no-cache' });
        this.catalog = await r.json();
      } catch (e) {
        console.error('Failed to load belt-catalog.json', e);
      } finally {
        this.loading = false;
      }
    },

    // -------- Computed --------
    get visibleSteps() {
      return this.steps.filter((s) => !s.skipIf || !s.skipIf(this.selections));
    },
    get currentStepDef() {
      return this.visibleSteps[this.currentStep - 1] || null;
    },
    get isComplete() {
      return this.currentStep > this.visibleSteps.length;
    },
    get hasSelections() {
      return Object.values(this.selections).some((v) => v !== null);
    },
    get currentOptions() {
      if (!this.currentStepDef) return [];
      return this.optionsFor(this.currentStepDef.key);
    },

    // -------- Filtering --------
    matchingProducts(ignoreKey = null) {
      if (!this.catalog) return [];
      return this.catalog.products.filter((p) => {
        if (ignoreKey !== 'width' && this.selections.width !== null && p.width !== this.selections.width) return false;
        if (ignoreKey !== 'color' && this.selections.color !== null && !p.colors_available.includes(this.selections.color)) return false;
        if (ignoreKey !== 'withBuckle' && this.selections.withBuckle !== null && p.has_buckle !== this.selections.withBuckle) return false;
        if (ignoreKey !== 'buckleStyle' && this.selections.buckleStyle !== null && !p.buckle_styles.includes(this.selections.buckleStyle)) return false;
        if (ignoreKey !== 'hardwareType' && this.selections.hardwareType !== null && !p.hardware_type.includes(this.selections.hardwareType)) return false;
        if (ignoreKey !== 'hardwareColor' && this.selections.hardwareColor !== null && !p.hardware_colors.includes(this.selections.hardwareColor)) return false;
        if (ignoreKey !== 'size' && this.selections.size !== null && !p.sizes_available.includes(this.selections.size)) return false;
        return true;
      });
    },

    optionsFor(key) {
      const matches = this.matchingProducts(key);
      if (key === 'width') {
        const values = [...new Set(matches.map((p) => p.width))].sort((a, b) => a - b);
        return values.map((v) => ({ value: v, label: v + '”', count: matches.filter((p) => p.width === v).length }));
      }
      if (key === 'color') {
        const values = [...new Set(matches.flatMap((p) => p.colors_available))].sort();
        return values.map((v) => ({
          value: v,
          label: v,
          swatch: this.colorSwatch(v),
          image: this.colorImage(v, matches),
          count: matches.filter((p) => p.colors_available.includes(v)).length,
        }));
      }
      if (key === 'withBuckle') {
        const hasYes = matches.some((p) => p.has_buckle === true);
        const hasNo = matches.some((p) => p.has_buckle === false);
        const out = [];
        if (hasYes) out.push({ value: true, label: 'With buckle', image: null });
        if (hasNo) out.push({ value: false, label: 'No buckle (strap only)', image: null });
        return out;
      }
      if (key === 'buckleStyle') {
        const values = [...new Set(matches.flatMap((p) => p.buckle_styles))].sort();
        return values.map((v) => ({ value: v, label: v, count: matches.filter((p) => p.buckle_styles.includes(v)).length }));
      }
      if (key === 'hardwareType') {
        const values = [...new Set(matches.flatMap((p) => p.hardware_type))].sort();
        return values.map((v) => ({ value: v, label: v, count: matches.filter((p) => p.hardware_type.includes(v)).length }));
      }
      if (key === 'hardwareColor') {
        const values = [...new Set(matches.flatMap((p) => p.hardware_colors))].sort();
        return values.map((v) => ({ value: v, label: v, swatch: this.hardwareSwatch(v), count: matches.filter((p) => p.hardware_colors.includes(v)).length }));
      }
      if (key === 'size') {
        const values = [...new Set(matches.flatMap((p) => p.sizes_available))];
        // Sort sizes numerically when possible (e.g. "32 inches")
        values.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
        return values.map((v) => ({ value: v, label: v, count: 0 }));
      }
      return [];
    },

    // -------- Actions --------
    select(key, value) {
      this.selections[key] = value;
      this.currentStep++;
      this.trackEvent(key, value);
    },
    back() {
      if (this.currentStep <= 1) return;
      this.currentStep--;
      const stepDef = this.visibleSteps[this.currentStep - 1];
      if (stepDef) this.selections[stepDef.key] = null;
    },
    jumpTo(stepNum) {
      // Allow jumping to any already-completed step (resets selections beyond it)
      if (stepNum < 1 || stepNum > this.currentStep) return;
      for (let i = stepNum - 1; i < this.visibleSteps.length; i++) {
        this.selections[this.visibleSteps[i].key] = null;
      }
      this.currentStep = stepNum;
    },
    reset() {
      Object.keys(this.selections).forEach((k) => (this.selections[k] = null));
      this.currentStep = 1;
    },

    // -------- Result --------
    resolveResult() {
      const matches = this.matchingProducts();
      if (matches.length !== 1) return null;
      const p = matches[0];

      // Build variant key from product's option order (sorted alphabetically — must match builder)
      const optEntries = [];
      if (p.colors_available.length) optEntries.push(['Color', this.selections.color]);
      if (p.buckle_finishes_available.length) optEntries.push(['Belt buckle', this.selections.hardwareColor]);
      if (p.sizes_available.length) optEntries.push(['Size', this.selections.size]);
      optEntries.sort((a, b) => a[0].localeCompare(b[0]));
      const key = optEntries.map((o) => o[1]).join('|');
      const variantGid = p.variants_index[key];
      return { product: p, variantGid, key };
    },
    resultPriceLabel() {
      const r = this.resolveResult();
      if (!r) return '';
      const cur = r.product.price_range.currency || 'USD';
      const sym = cur === 'USD' ? '$' : '';
      return `${sym}${r.product.price_range.min.toFixed(2)}`;
    },
    previewImageUrl() {
      if (!this.catalog) return null;
      const r = this.resolveResult();
      const product = r?.product || this.matchingProducts()[0];
      if (!product) return null;

      // Smart pick from structured media[] using current selections.
      // Some fields (hw, buckle) may be arrays — match if ANY value overlaps.
      if (product.media?.length) {
        const need = {
          color: this.selections.color,
          hw: this.selections.hardwareType,
          hw_color: this.selections.hardwareColor,
          buckle: this.selections.buckleStyle,
        };
        const SCORE = { color: 10, hw_color: 5, buckle_color: 5, buckle: 3, hw: 1 };
        let best = { score: -1, url: null };
        for (const m of product.media) {
          let s = 0, conflict = false;
          for (const k of Object.keys(SCORE)) {
            if (!need[k]) continue;
            if (m[k] == null || (Array.isArray(m[k]) && !m[k].length)) continue;
            const mvList = Array.isArray(m[k])
              ? m[k].map(v => String(v).toLowerCase())
              : String(m[k]).toLowerCase().split(/\s*,\s*/).filter(Boolean);
            const nvLower = String(need[k]).toLowerCase();
            if (mvList.includes('all')) { s += Math.floor(SCORE[k] / 2); continue; }
            if (mvList.includes(nvLower)) { s += SCORE[k]; continue; }
            conflict = true; break;
          }
          if (!conflict && s > best.score) best = { score: s, url: m.url };
        }
        if (best.url) return best.url;
      }

      // Legacy fallback
      if (r?.product) {
        const cbKey = `${this.selections.color}|${this.selections.hardwareColor}`;
        return product.media_by_color_buckle?.[cbKey] || product.media_by_color?.[this.selections.color] || null;
      }
      return product.media_by_color?.[this.selections.color] || null;
    },
    previewAlt() {
      return [this.selections.color, this.selections.buckleStyle, 'belt'].filter(Boolean).join(' ');
    },
    previewCaption() {
      const matches = this.matchingProducts();
      if (this.isComplete && this.resolveResult()) return this.resolveResult().product.title;
      if (matches.length === 1) return `${matches.length} match: ${matches[0].title}`;
      return `${matches.length} matching ${matches.length === 1 ? 'product' : 'products'}`;
    },

    // -------- Cart actions --------
    async addToCart() {
      const r = this.resolveResult();
      if (!r?.variantGid) {
        alert('Variant not found. Please review your selections.');
        return;
      }
      const variantId = r.variantGid.split('/').pop();
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        if (!res.ok) throw new Error(await res.text());
        // Notify theme cart drawer
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
        const drawer = document.querySelector('cart-drawer, [data-cart-drawer]');
        drawer?.show?.();
        drawer?.open?.();
        this.trackEvent('added_to_cart', { variantId, productHandle: r.product.handle });
      } catch (e) {
        console.error('Add to cart failed', e);
        alert('Failed to add to cart. Please try again.');
      }
    },
    viewProduct() {
      const r = this.resolveResult();
      if (!r) return;
      const variantId = r.variantGid.split('/').pop();
      window.location.href = `/products/${r.product.handle}?variant=${variantId}`;
    },

    // -------- Helpers --------
    formatValue(key, value) {
      const def = this.steps.find((s) => s.key === key);
      return def?.formatter ? def.formatter(value) : value;
    },
    colorSwatch(name) {
      const map = {
        'Black': '#1a1a1a',
        'Brown': '#5a3a22',
        'Crazy Horse': '#8b5a2b',
        'Crazy Horse Red': '#7a2418',
        'Crazy Horse Green': '#3a4a26',
        'Crazy Horse Gray': '#5a5a5a',
        'Antique Brown': '#6b4a2b',
        'Antique Chocolate': '#3a2010',
        'Golden Brown': '#a07040',
        'White': '#f4ecdc',
      };
      return map[name] || null;
    },
    colorImage(name, matches) {
      for (const p of matches) {
        if (p.media_by_color[name]) return p.media_by_color[name];
      }
      return null;
    },
    hardwareSwatch(name) {
      const map = {
        'Nickel': 'linear-gradient(135deg, #d8d8d8, #a8a8a8)',
        'Gold': 'linear-gradient(135deg, #f5d97a, #c89a2c)',
        'Antique brass': 'linear-gradient(135deg, #b39157, #6e4e2a)',
        'Black': '#222',
      };
      return map[name] || null;
    },
    trackEvent(key, value) {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'belt_configurator_step',
          step: this.currentStep,
          dimension: key,
          value: typeof value === 'object' ? JSON.stringify(value) : String(value),
        });
      }
    },
  };
};
